import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, IconButton, Alert, CircularProgress, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, FormControlLabel, Divider, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add, Edit, Delete, LockOpen, Lock, Key, ManageAccounts, Search,
  CheckCircle as CheckCircleIcon, RadioButtonUnchecked,
} from '@mui/icons-material';
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

// ─── tipos ───────────────────────────────────────────────────

interface Permissao {
  id: number; codigo: string; modulo: string; descricao: string; ordem: number;
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
  perfil_base: string;
  ativo: boolean;
  senha_temporaria: boolean;
  ultimo_login: string | null;
  criado_em: string | null;
  bloqueado_ate: string | null;
  tentativas_falhas: number;
  total_permissoes: number;
}

interface UsuarioDetalhe extends Usuario {
  permissoes: string[];
}

interface UsuariosProps { onVoltar: () => void; }

// ─── constantes visuais ──────────────────────────────────────

const HEADER_SX = {
  backgroundColor: '#1D1D1F', color: 'white', fontWeight: 700,
  fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5, py: 1,
};

const PERFIS = ['admin', 'supervisor', 'agente', 'visualizador', 'personalizado'];

const PERFIL_COR: Record<string, { bg: string; text: string }> = {
  admin:         { bg: '#FEE2E2', text: '#991B1B' },
  supervisor:    { bg: '#EDE9FE', text: '#4C1D95' },
  agente:        { bg: '#D1FAE5', text: '#065F46' },
  visualizador:  { bg: '#FEF3C7', text: '#92400E' },
  personalizado: { bg: '#E0F2FE', text: '#0369A1' },
};

const fmtData = (s?: string | null) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── componente principal ────────────────────────────────────

export default function Usuarios({ onVoltar }: UsuariosProps) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [permissoesCatalogo, setPermissoesCatalogo] = useState<Permissao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  // filtros
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState<'todos' | 'ativos' | 'inativos'>('todos');

  // dialogs
  const [dialogForm, setDialogForm] = useState(false);
  const [dialogPerms, setDialogPerms] = useState(false);
  const [dialogDeletar, setDialogDeletar] = useState(false);
  const [dialogSenha, setDialogSenha] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState('');

  // dados dos dialogs
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [alvo, setAlvo] = useState<Usuario | null>(null);
  const [permsSelecionadas, setPermsSelecionadas] = useState<string[]>([]);

  // form de criação/edição
  const [form, setForm] = useState({
    nome: '', email: '', senha: '', perfil_base: 'agente', ativo: true, senha_temporaria: false,
  });
  const [formErro, setFormErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // ─── carregamento ──────────────────────────────────────────

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [uResp, pResp] = await Promise.all([
        axios.get(`${API_URL}/api/usuarios/`, { headers }),
        axios.get(`${API_URL}/api/usuarios/permissoes`, { headers }),
      ]);
      setUsuarios(uResp.data);
      setPermissoesCatalogo(pResp.data);
    } catch {
      setErro('Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const limparMensagens = () => { setErro(''); setSucesso(''); };

  // ─── filtro local ──────────────────────────────────────────

  const usuariosFiltrados = usuarios.filter(u => {
    const matchBusca = !busca ||
      u.nome.toLowerCase().includes(busca.toLowerCase()) ||
      u.email.toLowerCase().includes(busca.toLowerCase());
    const matchAtivo =
      filtroAtivo === 'todos' ? true :
      filtroAtivo === 'ativos' ? u.ativo : !u.ativo;
    return matchBusca && matchAtivo;
  });

  // ─── form criar/editar ─────────────────────────────────────

  const abrirNovo = () => {
    setEditando(null);
    setForm({ nome: '', email: '', senha: '', perfil_base: 'agente', ativo: true, senha_temporaria: false });
    setFormErro('');
    setDialogForm(true);
  };

  const abrirEditar = (u: Usuario) => {
    setEditando(u);
    setForm({ nome: u.nome, email: u.email, senha: '', perfil_base: u.perfil_base, ativo: u.ativo, senha_temporaria: u.senha_temporaria });
    setFormErro('');
    setDialogForm(true);
  };

  const salvarForm = async () => {
    setFormErro('');
    setSalvando(true);
    try {
      if (editando) {
        await axios.put(`${API_URL}/api/usuarios/${editando.id}`, {
          nome: form.nome, email: form.email, perfil_base: form.perfil_base, ativo: form.ativo,
        }, { headers });
        setSucesso(`Usuário "${form.nome}" atualizado.`);
      } else {
        await axios.post(`${API_URL}/api/usuarios/`, {
          nome: form.nome, email: form.email, senha: form.senha,
          perfil_base: form.perfil_base, permissoes_codigos: [],
          ativo: form.ativo, senha_temporaria: form.senha_temporaria,
        }, { headers });
        setSucesso(`Usuário "${form.nome}" criado.`);
      }
      setDialogForm(false);
      carregar();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Erro 422 do Pydantic — extrai a primeira mensagem legível
        const primeiro = detail[0];
        const campo = primeiro?.loc?.slice(1).join('.') || '';
        const msg = primeiro?.msg || 'Erro de validação';
        setFormErro(campo ? `${campo}: ${msg}` : msg);
      } else {
        setFormErro(detail || 'Erro ao salvar.');
      }
    } finally {
      setSalvando(false);
    }
  };

  // ─── permissões ────────────────────────────────────────────

  const abrirPerms = async (u: Usuario) => {
    try {
      const resp = await axios.get<UsuarioDetalhe>(`${API_URL}/api/usuarios/${u.id}`, { headers });
      setAlvo(u);
      setPermsSelecionadas(resp.data.permissoes);
      setDialogPerms(true);
    } catch {
      setErro('Erro ao carregar permissões.');
    }
  };

  const salvarPerms = async () => {
    if (!alvo) return;
    setSalvando(true);
    try {
      await axios.put(`${API_URL}/api/usuarios/${alvo.id}/permissoes`,
        { permissoes_codigos: permsSelecionadas }, { headers });
      setSucesso(`Permissões de "${alvo.nome}" atualizadas.`);
      setDialogPerms(false);
      carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Erro ao salvar permissões.');
    } finally {
      setSalvando(false);
    }
  };

  const togglePerm = (codigo: string) => {
    setPermsSelecionadas(prev =>
      prev.includes(codigo) ? prev.filter(c => c !== codigo) : [...prev, codigo]
    );
  };

  const toggleModulo = (modulo: string) => {
    const codigos = permissoesCatalogo.filter(p => p.modulo === modulo).map(p => p.codigo);
    const todosMarcados = codigos.every(c => permsSelecionadas.includes(c));
    if (todosMarcados) {
      setPermsSelecionadas(prev => prev.filter(c => !codigos.includes(c)));
    } else {
      setPermsSelecionadas(prev => prev.concat(codigos.filter(c => !prev.includes(c))));
    }
  };

  // ─── bloquear / desbloquear ────────────────────────────────

  const toggleBloqueio = async (u: Usuario) => {
    const endpoint = u.ativo ? 'bloquear' : 'desbloquear';
    const acao = u.ativo ? 'bloqueado' : 'desbloqueado';
    try {
      await axios.post(`${API_URL}/api/usuarios/${u.id}/${endpoint}`, {}, { headers });
      setSucesso(`Usuário "${u.nome}" ${acao}.`);
      carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Erro ao alterar status.');
    }
  };

  // ─── resetar senha ─────────────────────────────────────────

  const resetarSenha = async (u: Usuario) => {
    try {
      const resp = await axios.post(`${API_URL}/api/usuarios/${u.id}/resetar-senha`, {}, { headers });
      setSenhaGerada(resp.data.senha_temporaria);
      setDialogSenha(true);
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Erro ao resetar senha.');
    }
  };

  // ─── excluir ───────────────────────────────────────────────

  const confirmarDeletar = (u: Usuario) => { setAlvo(u); setDialogDeletar(true); };

  const excluir = async () => {
    if (!alvo) return;
    setSalvando(true);
    try {
      await axios.delete(`${API_URL}/api/usuarios/${alvo.id}`, { headers });
      setSucesso(`Usuário "${alvo.nome}" excluído.`);
      setDialogDeletar(false);
      carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Erro ao excluir.');
      setDialogDeletar(false);
    } finally {
      setSalvando(false);
    }
  };

  // ─── render ────────────────────────────────────────────────

  if (carregando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  // Agrupar permissões por módulo para o dialog
  const modulos = permissoesCatalogo
    .map(p => p.modulo)
    .filter((m, i, arr) => arr.indexOf(m) === i)
    .sort();

  return (
    <Box sx={{ backgroundColor: '#F5F5F7', minHeight: '100vh' }}>
      <ModuleHeader
        titulo="Usuários"
        subtitulo="Gerenciamento de acesso ao sistema"
        icone={<ManageAccounts sx={{ fontSize: 28 }} />}
        onVoltar={onVoltar}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={abrirNovo}
            sx={{ backgroundColor: '#0071E3', '&:hover': { backgroundColor: '#0056b3' }, borderRadius: 2 }}>
            Novo Usuário
          </Button>
        </Box>

        {erro && <Alert severity="error" onClose={limparMensagens} sx={{ mb: 2 }}>{erro}</Alert>}
        {sucesso && <Alert severity="success" onClose={limparMensagens} sx={{ mb: 2 }}>{sucesso}</Alert>}

        {/* filtros */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Buscar por nome ou e-mail…"
            value={busca} onChange={e => setBusca(e.target.value)}
            sx={{ flex: 1, minWidth: 260, backgroundColor: 'white', borderRadius: 2 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
          />
          {(['todos', 'ativos', 'inativos'] as const).map(f => (
            <Button key={f} variant={filtroAtivo === f ? 'contained' : 'outlined'}
              size="small" onClick={() => setFiltroAtivo(f)}
              sx={{ borderRadius: 2, textTransform: 'capitalize',
                ...(filtroAtivo === f ? { backgroundColor: '#1D1D1F' } : {}) }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
          <Typography variant="body2" sx={{ alignSelf: 'center', color: '#8E8E93' }}>
            {usuariosFiltrados.length} usuário{usuariosFiltrados.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* tabela */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Nome', 'E-mail', 'Perfil', 'Permissões', 'Último acesso', 'Status', 'Ações'].map(col => (
                  <TableCell key={col} sx={HEADER_SX}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {usuariosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#8E8E93' }}>
                    Nenhum usuário encontrado.
                  </TableCell>
                </TableRow>
              ) : usuariosFiltrados.map((u, i) => (
                <TableRow key={u.id} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#F7F7F8',
                  '&:hover': { filter: 'brightness(0.97)' } }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {u.nome}
                    {u.senha_temporaria && (
                      <Chip label="senha temp." size="small"
                        sx={{ ml: 1, fontSize: 10, backgroundColor: '#FEF3C7', color: '#92400E' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#3C3C43', fontSize: 13 }}>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={u.perfil_base} size="small"
                      sx={{ backgroundColor: PERFIL_COR[u.perfil_base]?.bg || '#E5E5EA',
                        color: PERFIL_COR[u.perfil_base]?.text || '#1D1D1F',
                        fontWeight: 600, fontSize: 11 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={u.total_permissoes} size="small"
                      sx={{ backgroundColor: '#E0F2FE', color: '#0369A1', fontWeight: 700, fontSize: 12 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#8E8E93' }}>{fmtData(u.ultimo_login)}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.ativo ? 'Ativo' : 'Inativo'}
                      size="small"
                      sx={{
                        backgroundColor: u.ativo ? '#D1FAE5' : '#FEE2E2',
                        color: u.ativo ? '#065F46' : '#991B1B',
                        fontWeight: 600, fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(u)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Permissões">
                        <IconButton size="small" onClick={() => abrirPerms(u)} sx={{ color: '#5856D6' }}>
                          <ManageAccounts fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resetar senha">
                        <IconButton size="small" onClick={() => resetarSenha(u)} sx={{ color: '#FF9500' }}>
                          <Key fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={u.ativo ? 'Bloquear' : 'Desbloquear'}>
                        <IconButton size="small" onClick={() => toggleBloqueio(u)}
                          sx={{ color: u.ativo ? '#FF3B30' : '#34C759' }}>
                          {u.ativo ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => confirmarDeletar(u)} sx={{ color: '#FF3B30' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Dialog: Criar / Editar ── */}
      <Dialog open={dialogForm} onClose={() => setDialogForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editando ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formErro && <Alert severity="error">{formErro}</Alert>}
          <TextField label="Nome completo" fullWidth size="small" value={form.nome}
            onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
          <TextField label="E-mail" fullWidth size="small" type="email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          {!editando && (
            <Box>
              <TextField
                label="Senha"
                fullWidth size="small" type="password" value={form.senha}
                onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                placeholder="Digite uma senha segura"
              />
              <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#F9FAFB', borderRadius: 1.5, border: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Requisitos de Senha
                </Typography>
                {[
                  { regra: 'Mínimo 12 caracteres', valido: form.senha.length >= 12 },
                  { regra: 'Pelo menos 1 letra maiúscula (A-Z)', valido: /[A-Z]/.test(form.senha) },
                  { regra: 'Pelo menos 1 letra minúscula (a-z)', valido: /[a-z]/.test(form.senha) },
                  { regra: 'Pelo menos 1 número (0-9)', valido: /[0-9]/.test(form.senha) },
                  { regra: 'Pelo menos 1 símbolo (!@#$%&*+-=?_.)', valido: /[!@#$%&*+\-=?_.]/.test(form.senha) },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                    {item.valido
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      : <RadioButtonUnchecked sx={{ fontSize: 16, color: '#9CA3AF' }} />
                    }
                    <Typography sx={{ fontSize: 12, color: item.valido ? '#065F46' : '#6B7280', fontWeight: item.valido ? 600 : 400 }}>
                      {item.regra}
                    </Typography>
                  </Box>
                ))}
                {form.senha.length > 0 && (() => {
                  const checks = [
                    form.senha.length >= 12,
                    /[A-Z]/.test(form.senha),
                    /[a-z]/.test(form.senha),
                    /[0-9]/.test(form.senha),
                    /[!@#$%&*+\-=?_.]/.test(form.senha),
                  ];
                  const total = checks.filter(Boolean).length;
                  const cores = ['#EF4444', '#F59E0B', '#F59E0B', '#3B82F6', '#3B82F6', '#10B981'];
                  const labels = ['Muito fraca', 'Fraca', 'Razoável', 'Boa', 'Forte', 'Excelente'];
                  return (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #D1D5DB', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, height: 6, backgroundColor: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${(total / 5) * 100}%`, height: '100%', backgroundColor: cores[total], transition: 'all 0.3s ease' }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: cores[total], minWidth: 80, textAlign: 'right' }}>
                        {labels[total]}
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
          <TextField label="Perfil base" select fullWidth size="small" value={form.perfil_base}
            onChange={e => setForm(f => ({ ...f, perfil_base: e.target.value }))}>
            {PERFIS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />}
              label="Ativo" />
            {!editando && (
              <FormControlLabel
                control={<Checkbox checked={form.senha_temporaria} onChange={e => setForm(f => ({ ...f, senha_temporaria: e.target.checked }))} />}
                label="Senha temporária (exige troca no 1º login)" />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogForm(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvarForm} disabled={salvando}
            sx={{ backgroundColor: '#0071E3', borderRadius: 2 }}>
            {salvando ? <CircularProgress size={18} /> : editando ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Permissões ── */}
      <Dialog open={dialogPerms} onClose={() => setDialogPerms(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Permissões — {alvo?.nome}
          <Typography variant="body2" color="text.secondary">
            {permsSelecionadas.length} de {permissoesCatalogo.length} permissões selecionadas
          </Typography>
        </DialogTitle>
        <DialogContent>
          {modulos.map(mod => {
            const permsDoMod = permissoesCatalogo.filter(p => p.modulo === mod);
            const qtdMarcados = permsDoMod.filter(p => permsSelecionadas.includes(p.codigo)).length;
            return (
              <Box key={mod} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={qtdMarcados === permsDoMod.length}
                        indeterminate={qtdMarcados > 0 && qtdMarcados < permsDoMod.length}
                        onChange={() => toggleModulo(mod)}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {mod}
                      </Typography>
                    }
                  />
                  <Chip label={`${qtdMarcados}/${permsDoMod.length}`} size="small"
                    sx={{ fontSize: 10, backgroundColor: '#E0F2FE', color: '#0369A1' }} />
                </Box>
                <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {permsDoMod.map(p => (
                    <FormControlLabel key={p.codigo}
                      sx={{ width: '50%', mr: 0 }}
                      control={
                        <Checkbox size="small" checked={permsSelecionadas.includes(p.codigo)}
                          onChange={() => togglePerm(p.codigo)} />
                      }
                      label={<Typography sx={{ fontSize: 12 }}>{p.descricao}</Typography>}
                    />
                  ))}
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogPerms(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvarPerms} disabled={salvando}
            sx={{ backgroundColor: '#0071E3', borderRadius: 2 }}>
            {salvando ? <CircularProgress size={18} /> : 'Salvar Permissões'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirmar exclusão ── */}
      <Dialog open={dialogDeletar} onClose={() => setDialogDeletar(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#FF3B30' }}>Excluir Usuário</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Esta ação é permanente e não pode ser desfeita.
          </Alert>
          <Typography>
            Confirma a exclusão de <strong>{alvo?.nome}</strong> ({alvo?.email})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogDeletar(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={excluir} disabled={salvando}>
            {salvando ? <CircularProgress size={18} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Senha gerada ── */}
      <Dialog open={dialogSenha} onClose={() => setDialogSenha(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Senha Temporária Gerada</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Anote agora — esta senha não será exibida novamente.
          </Alert>
          <Box sx={{ backgroundColor: '#1D1D1F', borderRadius: 2, p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 20, color: '#F5F5F7', letterSpacing: 2, fontWeight: 700 }}>
              {senhaGerada}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: '#8E8E93' }}>
            O usuário deverá trocar esta senha no próximo login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setDialogSenha(false)}
            sx={{ backgroundColor: '#1D1D1F', borderRadius: 2 }}>
            Entendido — já anotei
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
